import { definition, context, api, signal } from "@uesio/ui"
import { TextOptions } from "../../utilities/filetext/filetext"

import UserFile from "../../utilities/userfile/userfile"
import { UserFileMetadata } from "../field/field"

type FileDefinition = {
  id?: string
  displayAs?: string
  accept?: string
  mode?: context.FieldMode
  textOptions?: TextOptions
  // Signals to run after a file is uploaded
  onUploadSignals?: signal.SignalDefinition[]
  // Signals to run after a file is deleted
  onDeleteSignals?: signal.SignalDefinition[]
  allowUpload?: boolean
  allowDelete?: boolean
  editMode?: "FILE" | "DATA"
  showTile?: boolean
}

const FileAttachment: definition.UC<FileDefinition> = (props) => {
  const { context, definition } = props
  const {
    accept,
    displayAs,
    mode = context.getFieldMode(),
    onUploadSignals,
    onDeleteSignals,
    textOptions,
    allowUpload = false,
    allowDelete = false,
    editMode = "FILE",
    showTile = true,
  } = definition

  const id = api.component.getComponentIdFromProps(props)

  const record = context.getRecord()

  // If we don't have a record in context, bail
  if (!record) return null

  const userFile = record.source as UserFileMetadata

  // uesio/core.data is a special field for supporting editing the contents of a file via wire operations vs. file operations.
  // In practice, uesio/core.data should only be on a record/userfile when the intention is to edit the file contents directly,
  // however there may be situations where its included but content edit is not desired. Given its special case nature, we
  // ensure that editing the file contents is explicitly being requested.
  const allowChange = editMode === "DATA"

  return (
    <UserFile
      id={id}
      accept={accept}
      displayAs={displayAs}
      userFile={userFile}
      allowUpload={allowUpload}
      allowDelete={allowDelete}
      showTile={showTile}
      onUpload={
        !allowUpload
          ? undefined
          : async (uploadResult) => {
              record.setAll(uploadResult)
              if (onUploadSignals) {
                await api.signal.getHandler(
                  onUploadSignals,
                  context.addComponentFrame(props.componentType as string, {
                    file: uploadResult,
                  }),
                )?.()
              }
            }
      }
      onDelete={
        !allowDelete
          ? undefined
          : async (deleteResult) => {
              if (onDeleteSignals) {
                await api.signal.getHandler(
                  onDeleteSignals,
                  context.addComponentFrame(props.componentType as string, {
                    deleteResult,
                  }),
                )?.()
              }
            }
      }
      onChange={
        !allowChange
          ? undefined
          : (value) => {
              record.update("uesio/core.data", value, context)
            }
      }
      context={context}
      mode={mode}
      textOptions={textOptions}
    />
  )
}

export default FileAttachment
